/**

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('account-scripts') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};
 * Account Sidebar Management Script - SPA Version
 * Handles sidebar expand/collapse, client-side routing, and state persistence
 */

// Detect development mode based on hostname
const IS_DEVELOPMENT = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost') ||
                       window.location.hostname === '';

// Helper function: use debug logging (replaced with logger)
// Initialize logger with fallback
const logger = (typeof window !== 'undefined' && window.Logger) 
  ? window.Logger.create('account-scripts') 
  : {
      debug: () => {},
      info: (...args) => console.log('[account-scripts]', ...args),
      warn: (...args) => console.warn('[account-scripts]', ...args),
      error: (...args) => console.error('[account-scripts]', ...args)
    };

const accountDebug = (...args) => logger.debug(...args);

// Top-level log to verify script is loading - MUST be first thing
accountDebug('[account-scripts] ====== SCRIPT FILE LOADED ======');
accountDebug('[account-scripts] Timestamp:', new Date().toISOString());
accountDebug('[account-scripts] Location:', window.location.href);

// Wrap everything in try-catch to prevent silent failures
try {
  (function() {
    'use strict';
    
    accountDebug('[account-scripts] IIFE executing...');

  // Strip sensitive query params from URL on account page (passwords must never persist in URL/history)
  if (window.location.pathname.includes('account.html') && window.location.search) {
    try {
      const params = new URLSearchParams(window.location.search);
      const sensitive = ['currentPassword', 'newPassword', 'confirmPassword'];
      let changed = false;
      sensitive.forEach(key => {
        if (params.has(key)) {
          params.delete(key);
          changed = true;
        }
      });
      if (changed) {
        const cleanSearch = params.toString();
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash;
        window.history.replaceState(null, '', cleanUrl);
      }
    } catch (e) {
      accountDebug('[account-scripts] Could not strip sensitive URL params:', e);
    }
  }

  // Define modal close function immediately to support inline onclick handlers
  // This will be overridden by script.js when it loads, but provides immediate fallback
  if (typeof window.closeAddItemModalGlobal === 'undefined') {
    window.closeAddItemModalGlobal = function() {
      // Try to use AccountManager if available (preferred method)
      if (typeof AccountManager !== 'undefined' && typeof AccountManager.closeAddItemModalGlobal === 'function') {
        try {
          AccountManager.closeAddItemModalGlobal();
          return;
        } catch (error) {
          logger.error('Error in AccountManager.closeAddItemModalGlobal:', error);
          // Fall through to direct manipulation
        }
      }
      
      // Fallback: direct modal manipulation when AccountManager not available
      const modal = document.getElementById('addItemModal');
      if (!modal) {
        logger.warn('closeAddItemModalGlobal: addItemModal not found');
        return;
      }
      
      // Remove ESC key listener if it was added
      if (window._modalEscapeListenerFallback) {
        document.removeEventListener('keydown', window._modalEscapeListenerFallback);
        window._modalEscapeListenerFallback = null;
      }
      if (typeof AccountManager !== 'undefined' && typeof AccountManager.removeEscapeKeyListener === 'function') {
        AccountManager.removeEscapeKeyListener();
      }
      
      // Use same logic as hideAddItemModalUI for consistency
      // Remove inline styles that might interfere
      modal.style.removeProperty('opacity');
      modal.style.removeProperty('visibility');
      modal.style.removeProperty('pointer-events');
      
      // Keep display: flex during transition
      modal.style.display = 'flex';
      
      // Update state
      modal.dataset.state = 'closing';
      
      // Remove the is-visible class to trigger CSS transition
      modal.classList.remove('is-visible');
      
      // Hide completely after transition
      setTimeout(() => {
        modal.style.display = 'none';
        modal.dataset.state = 'closed';
      }, 300); // Slightly longer than CSS transition for safety
    };
  }
  
  // Add ESC key listener that works immediately (before script.js loads)
  // This ensures ESC key works even if modal is opened before AccountManager is ready
  if (!window._earlyModalEscapeListener) {
    window._earlyModalEscapeListener = function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        const modal = document.getElementById('addItemModal');
        if (modal && (modal.classList.contains('is-visible') || modal.dataset.state === 'open')) {
          window.closeAddItemModalGlobal();
        }
      }
    };
    document.addEventListener('keydown', window._earlyModalEscapeListener);
  }

  // --- Embedded Sidebar HTML Templates ---
  const guestSidebarHTML = `<aside class="account-sidebar" id="accountSidebar">
  <button class="sidebar-toggle" id="account-sidebar-toggle" aria-label="Toggle sidebar">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  </button>
  <nav class="sidebar-site-nav">
    <button class="sidebar-link sidebar-add-item-btn" id="sidebarAddItemBtn" data-action="open-add-item-modal" data-tooltip="Add New Item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span class="nav-text">Add Item</span>
    </button>
    <a href="trusted-finder.html" class="sidebar-link" data-tooltip="Trusted Finder">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 12l2 2 4-4"></path>
        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
        <path d="M12 21c0-1-1-3-3-3s-3 2-3 3 1 3 3 3 3-2 3-3"></path>
        <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
      </svg>
      <span class="nav-text">Trusted Finder</span>
    </a>
    <a href="search.html" class="sidebar-link" data-tooltip="Search">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
      </svg>
      <span class="nav-text">Search</span>
    </a>
    <a href="categories.html" class="sidebar-link" data-tooltip="Categories">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
      <span class="nav-text">Categories</span>
    </a>
  </nav>
  <nav class="sidebar-nav">
    <a href="index.html" class="sidebar-link" data-tooltip="Home">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
      <span class="nav-text">Home</span>
    </a>
    <a href="login.html" class="sidebar-link" data-tooltip="Sign In">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
        <polyline points="10 17 15 12 10 7"></polyline>
        <line x1="15" y1="12" x2="3" y2="12"></line>
      </svg>
      <span class="nav-text">Sign In</span>
    </a>
    <a href="register.html" class="sidebar-link" data-tooltip="Sign Up">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="8.5" cy="7" r="4"></circle>
        <line x1="20" y1="8" x2="20" y2="14"></line>
        <line x1="23" y1="11" x2="17" y2="11"></line>
      </svg>
      <span class="nav-text">Sign Up</span>
    </a>
  </nav>
</aside>`;

  const loggedInSidebarHTML = `<aside class="account-sidebar" id="accountSidebar">
  <button class="sidebar-toggle" id="account-sidebar-toggle" aria-label="Toggle sidebar">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  </button>
  <nav class="sidebar-site-nav">
    <button class="sidebar-link sidebar-add-item-btn" id="sidebarAddItemBtn" data-action="open-add-item-modal" data-tooltip="Add New Item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span class="nav-text">Add Item</span>
    </button>
    <a href="trusted-finder.html" class="sidebar-link" data-tooltip="Trusted Finder">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 12l2 2 4-4"></path>
        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
        <path d="M12 21c0-1-1-3-3-3s-3 2-3 3 1 3 3 3 3-2 3-3"></path>
        <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
      </svg>
      <span class="nav-text">Trusted Finder</span>
    </a>
    <a href="search.html" class="sidebar-link" data-tooltip="Search">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
      </svg>
      <span class="nav-text">Search</span>
    </a>
    <a href="categories.html" class="sidebar-link" data-tooltip="Categories">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
      <span class="nav-text">Categories</span>
    </a>
  </nav>
  <div class="sidebar-header">
    <h2>My Account</h2>
  </div>
  <div class="sidebar-profile" id="sidebarProfile">
    <a href="account.html#dashboard" class="sidebar-profile-avatar" data-route="dashboard" title="Go to Account Dashboard">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    </a>
    <div class="sidebar-profile-info">
      <div class="sidebar-profile-name" id="sidebarProfileName">Loading...</div>
      <div class="sidebar-profile-level" id="sidebarProfileLevel">Newfinder</div>
    </div>
    <div class="sidebar-profile-stats">
      <div class="sidebar-stat">
        <span class="sidebar-stat-value" id="sidebarStatItems">0</span>
        <span class="sidebar-stat-label">Items</span>
      </div>
      <div class="sidebar-stat">
        <span class="sidebar-stat-value" id="sidebarStatVotes">0</span>
        <span class="sidebar-stat-label">Votes</span>
      </div>
    </div>
    <div class="sidebar-profile-badges" id="sidebarProfileBadges">
      <!-- Badges will be populated here -->
    </div>
  </div>
  <nav class="sidebar-nav">
    <a href="account.html#dashboard" class="sidebar-link" data-route="dashboard" data-tooltip="Dashboard">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
      <span class="nav-text">Dashboard</span>
    </a>
    <a href="account.html#email-preferences" class="sidebar-link" data-route="email-preferences" data-tooltip="Email Preferences">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
      <span class="nav-text">Email Preferences</span>
    </a>
    <a href="account.html#activity" class="sidebar-link" data-route="activity" data-tooltip="Activity Center">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
      <span class="nav-text">Activity Center</span>
    </a>
    <a href="account.html#settings" class="sidebar-link" data-route="settings" data-tooltip="Settings">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
      </svg>
      <span class="nav-text">Settings</span>
    </a>
    <a href="#" class="sidebar-link" id="add-item-link" data-action="open-add-item-modal" data-tooltip="Add New Item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span class="nav-text">Add Item</span>
    </a>
  </nav>
  <div class="sidebar-footer">
    <button class="sidebar-signout-btn" id="sidebarSignOutBtn" title="Sign Out" aria-label="Sign Out">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
      </svg>
      <span class="nav-text">Sign Out</span>
    </button>
  </div>
</aside>`;

  // --- Client-Side Router ---
  const routes = {
    dashboard: 'dashboard-view',
    feed: 'dashboard-view',
    following: 'following-view',
    alerts: 'alerts-view',
    'email-preferences': 'email-preferences-view',
    activity: 'activity-view',
    settings: 'settings-view'
  };

  function getCurrentRoute() {
    const hash = window.location.hash.slice(1) || 'dashboard';
    if (hash === 'feed') return 'dashboard';
    // The Console calls this page the Watchlist, and watched products live in
    // the Following view alongside followed categories. Without the alias
    // `#watchlist` is not a route, so it fell through to the dashboard and the
    // pane's own first link went somewhere else than it said.
    if (hash === 'watchlist') return 'following';
    return hash in routes ? hash : 'dashboard';
  }

  function navigateToRoute(route) {
    // If we're not on account.html, navigate to account.html with the route
    if (!window.location.pathname.includes('account.html')) {
      window.location.href = `account.html#${route}`;
      return;
    }
    
    // Update URL hash without page reload
    window.location.hash = route;
    renderRoute(route);
  }

  function renderRoute(route) {
    // Hide all views (class + clear legacy inline display from older router code)
    Object.values(routes).forEach(viewId => {
      const view = document.getElementById(viewId);
      if (view) {
        view.classList.remove('is-account-view-active');
        view.style.removeProperty('display');
      }
    });

    // Show the current route's view
    const viewId = routes[route];
    const view = document.getElementById(viewId);
    if (view) {
      view.classList.add('is-account-view-active');
      view.style.removeProperty('display');
    }

    // Update active states
    updateActiveState(route);

    // Refresh account data when navigating to a view
    // This ensures data is up-to-date when switching views
    setTimeout(async () => {
      if (window.accountManager) {
        try {
          if (route === 'dashboard' || route === 'feed') {
            if (typeof window.accountManager.updateDashboard === 'function') {
              await window.accountManager.updateDashboard();
            }
          } else if (route === 'following') {
            if (typeof window.accountManager.updateFollowingView === 'function') {
              await window.accountManager.updateFollowingView();
            }
            if (typeof window.accountManager.updateWatchedProductsView === 'function') {
              await window.accountManager.updateWatchedProductsView();
            }
          } else if (route === 'alerts') {
            if (typeof window.accountManager.updateAlertsView === 'function') {
              await window.accountManager.updateAlertsView();
            }
          } else if (route === 'email-preferences') {
            if (typeof window.accountManager.updateEmailPreferencesView === 'function') {
              window.accountManager.updateEmailPreferencesView();
            }
          } else if (route === 'activity') {
            if (typeof window.accountManager.updateActivityView === 'function') {
              window.accountManager.updateActivityView();
            }
          } else if (route === 'settings') {
            if (typeof window.accountManager.updateSettingsView === 'function') {
              window.accountManager.updateSettingsView();
            }
          }
          
          // Update sidebar profile on navigation
          if (typeof updateSidebarProfile === 'function') {
            try {
              await updateSidebarProfile();
            } catch (profileError) {
              logger.warn('Could not update sidebar profile:', profileError);
            }
          }
        } catch (error) {
          logger.warn('Error updating dashboard on navigation:', error);
          // Don't break navigation - continue anyway
        }
      } else {
        // Retry after a short delay if AccountManager isn't ready yet
        setTimeout(async () => {
          if (window.accountManager) {
            try {
              if (route === 'dashboard') {
                if (typeof window.accountManager.updateDashboard === 'function') {
                  await window.accountManager.updateDashboard();
                }
              } else if (route === 'saved') {
                if (typeof window.accountManager.updateSavedItemsView === 'function') {
                  window.accountManager.updateSavedItemsView();
                }
              } else if (route === 'email-preferences') {
                if (typeof window.accountManager.updateEmailPreferencesView === 'function') {
                  window.accountManager.updateEmailPreferencesView();
                }
              } else if (route === 'activity') {
                if (typeof window.accountManager.updateActivityView === 'function') {
                  window.accountManager.updateActivityView();
                }
              } else if (route === 'settings') {
                if (typeof window.accountManager.updateSettingsView === 'function') {
                  window.accountManager.updateSettingsView();
                }
              }
              
              // Update sidebar profile on navigation
              if (typeof updateSidebarProfile === 'function') {
                try {
                  await updateSidebarProfile();
                } catch (profileError) {
                  logger.warn('Could not update sidebar profile:', profileError);
                }
              }
            } catch (error) {
              logger.warn('Error updating dashboard on retry:', error);
            }
          }
        }, 200);
      }
    }, 50);

    // Scroll to top
    window.scrollTo(0, 0);
  }

  let routerInitialized = false;

  function setupRouter() {
    // Only set up router on account.html
    if (!window.location.pathname.includes('account.html')) {
      // Clear all sidebar active states on non-account pages
      updateActiveState(null);
      return;
    }

    // Idempotent: loadSidebar can run repeatedly (esp. under redesign shell where
    // the legacy sidebar never exists and the init retry loop keeps calling it).
    if (routerInitialized) {
      renderRoute(getCurrentRoute());
      return;
    }
    routerInitialized = true;
    
    // Handle initial route
    // Wait a bit for AccountManager to initialize (since script.js loads after account-scripts.js)
    setTimeout(() => {
      const initialRoute = getCurrentRoute();
      renderRoute(initialRoute);
      
      // If no hash, redirect to dashboard
      if (!window.location.hash) {
        window.location.hash = 'dashboard';
      }
    }, 100);

    // Handle hash changes (back/forward buttons, direct navigation)
    window.addEventListener('hashchange', () => {
      const route = getCurrentRoute();
      renderRoute(route);
    });

    // Handle sidebar navigation clicks
    document.querySelectorAll('.sidebar-link[data-route]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const route = link.getAttribute('data-route');
        if (route) {
          navigateToRoute(route);
        }
      });
    });

    // Handle in-content account subnav clicks (hash router under redesign shell)
    document.querySelectorAll('.account-subnav a[data-route]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const route = link.getAttribute('data-route');
        if (route) {
          navigateToRoute(route);
        }
      });
    });

    // Handle profile avatar click using event delegation (also uses router if on account page)
    const sidebarForAvatar = document.getElementById('accountSidebar');
    if (sidebarForAvatar && !sidebarForAvatar._profileAvatarListenerAdded) {
      sidebarForAvatar.addEventListener('click', (e) => {
        const profileAvatar = e.target.closest('.sidebar-profile-avatar[data-route]');
        if (profileAvatar) {
          // If we're on account.html, use the router for smooth navigation
          if (window.location.pathname.includes('account.html')) {
            e.preventDefault();
            const route = profileAvatar.getAttribute('data-route');
            if (route) {
              navigateToRoute(route);
            }
          }
          // Otherwise, let the default link behavior handle navigation
        }
      });
      sidebarForAvatar._profileAvatarListenerAdded = true;
    }

    // Handle Add Item modal trigger using event delegation
    // This ensures it works even if the button is recreated or if setupRouter runs before the button exists
    const sidebar = document.getElementById('accountSidebar');
    if (sidebar) {
      // Only set up the listener once to avoid duplicates
      if (!sidebar._addItemListenerAdded) {
        // Use event delegation on the sidebar container
        sidebar.addEventListener('click', (e) => {
          // Check if the clicked element is the add item link/button or a child of it
          const addItemLink = e.target.closest('#add-item-link, #sidebarAddItemBtn, .sidebar-add-item-btn');
          if (addItemLink) {
            e.preventDefault();
            e.stopPropagation();
            
            logger.info('Add item button clicked');
            
            // Fast modal opening function with optimized retry logic
            const openModalFast = () => {
              // Check all possible functions in priority order
              // First priority: try accountManager if available
              if (window.accountManager && typeof window.accountManager.openAddItemModal === 'function') {
                try {
                  window.accountManager.openAddItemModal();
                  return true; // Success
                } catch (error) {
                  logger.error('Error opening add item modal:', error);
                }
              }
              
              // Second priority: try global function (usually available first)
              if (typeof window.openAddItemModalGlobal === 'function') {
                try {
                  window.openAddItemModalGlobal();
                  return true; // Success
                } catch (error) {
                  logger.error('Error in openAddItemModalGlobal:', error);
                }
              }
              
              // Third priority: try AccountManager static method
              if (typeof AccountManager !== 'undefined' && typeof AccountManager.openAddItemModalGlobal === 'function') {
                try {
                  AccountManager.openAddItemModalGlobal();
                  return true; // Success
                } catch (error) {
                  logger.error('Error calling AccountManager.openAddItemModalGlobal:', error);
                }
              }
              
              return false; // Not available yet
            };
            
            // Try immediately first (synchronous check)
            if (openModalFast()) {
              return; // Success, we're done
            }
            
            // If not available, use fast retry with shorter intervals
            let retries = 0;
            const maxRetries = 20; // More retries but faster
            const retryInterval = 10; // 10ms instead of 100ms for much faster response
            
            const fastRetry = () => {
              if (openModalFast()) {
                return; // Success
              }
              
              retries++;
              if (retries < maxRetries) {
                // Use requestAnimationFrame for first few attempts (very fast), then setTimeout
                if (retries <= 3) {
                  requestAnimationFrame(fastRetry);
                } else {
                  setTimeout(fastRetry, retryInterval);
                }
              } else {
                // Final fallback: try direct modal manipulation
                logger.warn('Modal opening functions not available after fast retries, using direct fallback');
                const modal = document.getElementById('addItemModal');
                if (modal) {
                  modal.style.display = 'flex';
                  modal.classList.add('is-visible');
                  modal.dataset.state = 'open';
                  
                  // Add ESC key listener for fallback case
                  if (typeof AccountManager !== 'undefined' && typeof AccountManager.addEscapeKeyListener === 'function') {
                    AccountManager.addEscapeKeyListener();
                  } else {
                    // Fallback ESC listener when AccountManager not available
                    if (!window._modalEscapeListenerFallback) {
                      window._modalEscapeListenerFallback = function(e) {
                        if (e.key === 'Escape' || e.keyCode === 27) {
                          const modal = document.getElementById('addItemModal');
                          if (modal && (modal.classList.contains('is-visible') || modal.dataset.state === 'open')) {
                            window.closeAddItemModalGlobal();
                          }
                        }
                      };
                      document.addEventListener('keydown', window._modalEscapeListenerFallback);
                    }
                  }
                } else {
                  logger.error('addItemModal element not found in DOM');
                }
              }
            };
            
            // Start fast retry
            requestAnimationFrame(fastRetry);
          }
        });
        
        sidebar._addItemListenerAdded = true;
        logger.info('Add item button event delegation set up on sidebar');
      } else {
        logger.info('Add item button event delegation already set up');
      }
    } else {
      logger.warn('accountSidebar not found, cannot set up add item button handler');
    }
  }

  function updateActiveState(route) {
    // Update sidebar links - only on account.html
    if (window.location.pathname.includes('account.html')) {
      document.querySelectorAll('.sidebar-link[data-route]').forEach(link => {
        link.classList.remove('active');
        if (link.getAttribute('data-route') === route) {
          link.classList.add('active');
        }
      });

      // In-content account subnav (redesign shell — replaces legacy sidebar IA)
      document.querySelectorAll('.account-subnav a[data-route]').forEach(link => {
        const isActive = link.getAttribute('data-route') === route;
        link.classList.toggle('active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    } else {
      // On other pages, ensure no sidebar links are highlighted
      document.querySelectorAll('.sidebar-link[data-route]').forEach(link => {
        link.classList.remove('active');
      });
      document.querySelectorAll('.account-subnav a[data-route]').forEach(link => {
        link.classList.remove('active');
        link.removeAttribute('aria-current');
      });
    }
  }

  /**
   * Check if current user is an admin and redirect to admin login if on customer-facing page
   */
  async function checkAdminUserAndRedirect() {
    try {
      const currentPath = window.location.pathname;
      
      // Skip check on admin pages and public pages (path segment /admin = admin console)
      const isAdminPage = currentPath.includes('/admin');
      const isPublicPage = currentPath.includes('about.html') || 
                          currentPath.includes('help.html') || 
                          currentPath.includes('privacy.html') || 
                          currentPath.includes('terms.html');
      
      if (isAdminPage || isPublicPage) {
        return; // Don't check on admin or public pages
      }
      
      // Check if user is logged in
      if (!window.apiService || typeof window.apiService.getAccessToken !== 'function') {
        return; // API service not available yet
      }
      
      const accessToken = window.apiService.getAccessToken();
      if (!accessToken) {
        return; // Not logged in
      }
      
      // Fetch current user to check admin status
      try {
        const userData = await window.apiService.getCurrentUser();
        if (userData && userData.user) {
          const user = userData.user;
          const isAdmin = user.isSuperuser || user.isAdmin;
          
          if (isAdmin) {
            // Admin user detected on customer-facing page - redirect
            logger.warn('[account-scripts] Admin user detected on customer-facing page, redirecting to admin portal');
            if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
              window.apiService.clearAccessToken();
            }
            // Clear any stored user data
            localStorage.removeItem('usedfindr_user');
            window.location.href = 'admin-login.html?message=Admin accounts must access the site through the admin portal.';
          }
        }
      } catch (error) {
        // If error fetching user, might be expired token - ignore
        logger.debug('[account-scripts] Could not fetch user for admin check:', error);
      }
    } catch (error) {
      logger.error('[account-scripts] Error in checkAdminUserAndRedirect:', error);
    }
  }

  function clearSidebarLayoutState() {
    const existingSidebar = document.getElementById('accountSidebar');
    if (existingSidebar) {
      existingSidebar.remove();
    }
    if (document.body) {
      document.body.classList.remove('has-account-sidebar', 'account-sidebar-transitions-enabled', 'mobile-menu-open');
      document.body.style.overflow = '';
    }
    document.documentElement.classList.remove('account-sidebar-expanded');
  }

  function hasGlobalRedesignShell() {
    return !!document.querySelector('.app[data-site-shell="1"]');
  }

  // --- Main Sidebar Logic ---
  function loadSidebar() {
    accountDebug('[account-scripts] loadSidebar called');
    
    // Exclude admin console (path segment /admin); show sidebar on regular login/register pages
    const currentPath = window.location.pathname;
    const isAdminAuthPage = currentPath.includes('/admin');
    
    accountDebug('[account-scripts] Current path:', currentPath, 'isAdminAuthPage:', isAdminAuthPage);
    
    if (isAdminAuthPage) {
      // Remove sidebar if it exists on admin auth pages
      clearSidebarLayoutState();
      logger.info('[account-scripts] Admin auth page detected, not loading sidebar');
      return; // Don't load sidebar on admin auth pages
    }

    // Ensure body exists before trying to add class
    if (!document.body) {
      logger.warn('[account-scripts] document.body not available yet, retrying in 50ms...');
      setTimeout(loadSidebar, 50);
      return;
    }

    // Global redesign shell (rail + topbar): never inject the legacy account sidebar on main site pages.
    const hasGlobalShell = hasGlobalRedesignShell();
    if (hasGlobalShell) {
      clearSidebarLayoutState();
      accountDebug('[account-scripts] Global redesign shell detected, skipping account sidebar injection');
      // Account subnav (Dashboard / Email / Activity / Settings) still needs the hash router.
      // setupRouter used to live only on the legacy-sidebar path below, so it never ran here.
      if (window.location.pathname.includes('account.html')) {
        setupRouter();
      }
      return;
    }

    // Check if sidebar already exists and if it needs updating
    const existingSidebar = document.getElementById('accountSidebar');
    const isLoggedIn = checkIfUserLoggedIn();
    const currentIsLoggedIn = existingSidebar ? existingSidebar.dataset.userLoggedIn === 'true' : null;
    
    accountDebug('[account-scripts] Sidebar exists:', !!existingSidebar, 'isLoggedIn:', isLoggedIn, 'currentIsLoggedIn:', currentIsLoggedIn);
    
    // If sidebar exists and login status matches, don't reload
    if (existingSidebar && currentIsLoggedIn === isLoggedIn) {
      if (isLoggedIn) {
        // Logged-in users keep sidebar layout compensation.
        document.body.classList.add('has-account-sidebar');
      } else {
        // Anonymous users must not keep account sidebar layout classes.
        clearSidebarLayoutState();
      }
      accountDebug('[account-scripts] Sidebar already exists with correct login status, skipping reload');
      return;
    }
    
    // Remove existing sidebar if it needs to be updated
    if (existingSidebar) {
      accountDebug('[account-scripts] Removing existing sidebar to update login status');
      existingSidebar.remove();
    }

    // Anonymous users should not get account sidebar injection.
    if (!isLoggedIn) {
      clearSidebarLayoutState();
      accountDebug('[account-scripts] User is anonymous, account sidebar disabled');
      return;
    }

    // Logged-in users get the account sidebar.
    const sidebarHTML = loggedInSidebarHTML;
    accountDebug('[account-scripts] Using logged-in sidebar HTML');

    // Add class to body to indicate sidebar is present
    document.body.classList.add('has-account-sidebar');
    accountDebug('[account-scripts] Added has-account-sidebar class to body');

    // Inject sidebar as the first child of <body>
    try {
      document.body.insertAdjacentHTML('afterbegin', sidebarHTML);
      accountDebug('[account-scripts] Sidebar HTML injected');
    } catch (error) {
      logger.error('[account-scripts] Error injecting sidebar HTML:', error);
      return;
    }
    
    // Mark sidebar with login status for change detection
    const sidebar = document.getElementById('accountSidebar');
    if (sidebar) {
      sidebar.dataset.userLoggedIn = isLoggedIn.toString();
      accountDebug('[account-scripts] Sidebar found and marked with login status:', isLoggedIn);
      
      // Update sidebar profile if user is logged in
      if (isLoggedIn) {
        // Call once - debouncing and cooldown will handle timing
        if (typeof updateSidebarProfile === 'function') {
          updateSidebarProfile();
        }
      }
    } else {
      logger.error('[account-scripts] ERROR: Sidebar was not found after injection!');
      return;
    }
    
    // Restore sidebar state from localStorage
    // On mobile, don't auto-open sidebar from localStorage (always start closed)
    const isMobile = window.innerWidth <= 768;
    const savedState = localStorage.getItem('accountSidebarState');
    if (savedState === 'true' && !isMobile) {
      document.documentElement.classList.add('account-sidebar-expanded');
    } else {
      document.documentElement.classList.remove('account-sidebar-expanded');
      // Clear mobile state if it was saved
      if (isMobile && savedState === 'true') {
        localStorage.removeItem('accountSidebarState');
      }
    }
    
    // Setup all functionality
    setupSidebarToggle();
    setupSidebarSignOut();
    setupSidebarSignInForm();
    setupCursorTooltips();
    accountDebug('[account-scripts] Sidebar functionality setup complete');
    
    // Only setup router if logged in AND on account page
    if (isLoggedIn && window.location.pathname.includes('account.html')) {
      setupRouter();
    } else {
      // Clear any active states on non-account pages
      document.querySelectorAll('.sidebar-link[data-route]').forEach(link => {
        link.classList.remove('active');
      });
    }
    
    // Enable transitions after DOM is stable
    enableTransitionsAfterLoad();
    accountDebug('[account-scripts] Sidebar load complete!');
  }
  
  // Check if user is logged in
  function checkIfUserLoggedIn() {
    try {
      // Check window.accountManager first if available (most reliable)
      if (window.accountManager) {
        const currentUser = window.accountManager.getCurrentUser();
        if (currentUser && currentUser.id && currentUser.email) {
          return true;
        }
        // If accountManager exists but returns no user, they're logged out
        if (window.accountManager.currentUser === null || window.accountManager.currentUser === undefined) {
          return false;
        }
      }
      
      // Phase 3: access token from api-service memory only
      const accessToken = (window.apiService && typeof window.apiService.getAccessToken === 'function')
        ? window.apiService.getAccessToken()
        : null;
      if (accessToken) {
        const currentUser = localStorage.getItem('usedfindr_user');
        if (currentUser) {
          try {
            const user = JSON.parse(currentUser);
            if (user && user.id && user.email) {
              return true;
            }
          } catch (e) {
            if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
              window.apiService.clearAccessToken();
            }
            return false;
          }
        }
        if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
          window.apiService.clearAccessToken();
        }
        return false;
      }
      
      return false;
    } catch (e) {
      logger.error('Error checking login status:', e);
      return false;
    }
  }

  function setupSidebarToggle() {
    const toggleButton = document.getElementById('account-sidebar-toggle');
    if (!toggleButton) return;
    if (toggleButton._listenerAttached) return;
    toggleButton._listenerAttached = true;
    
    toggleButton.addEventListener('click', handleSidebarToggle);
    
    // Close sidebar on Escape key (especially useful on mobile)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.documentElement.classList.contains('account-sidebar-expanded')) {
        const isMobile = window.innerWidth <= 768;
        if (isMobile || document.getElementById('accountSidebar')) {
          handleSidebarToggle();
        }
      }
    });
  }

  function handleSidebarToggle() {
    hideCursorTooltip();
    
    const isMobile = window.innerWidth <= 768;
    const isCurrentlyExpanded = document.documentElement.classList.contains('account-sidebar-expanded');
    
    // Toggle the class on <html> - exactly like admin console
    const isExpanded = document.documentElement.classList.toggle('account-sidebar-expanded');
    
    // On mobile, sync backdrop visibility with sidebar state
    if (isMobile) {
      const overlay = document.getElementById('mobileMenuOverlay');
      if (overlay) {
        if (isExpanded) {
          overlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          document.body.classList.add('mobile-menu-open');
        } else {
          overlay.classList.remove('is-open');
          document.body.style.overflow = '';
          document.body.classList.remove('mobile-menu-open');
        }
      }
      
      // Update hamburger aria-expanded if it exists
      const hamburger = document.getElementById('mobileMenuToggle');
      if (hamburger) {
        hamburger.setAttribute('aria-expanded', isExpanded.toString());
      }
      
      // Don't persist mobile sidebar state (always start closed on mobile)
      if (!isExpanded) {
        localStorage.removeItem('accountSidebarState');
      }
    } else {
      // Desktop: save state to localStorage
      localStorage.setItem('accountSidebarState', isExpanded.toString());
    }
    
    // Log sidebar toggle at debug level to reduce console noise
    logger.debug('Sidebar toggled. Expanded:', isExpanded, 'Class present:', document.documentElement.classList.contains('account-sidebar-expanded'));
  }
  
  function setupSidebarSignOut() {
    const signOutBtn = document.getElementById('sidebarSignOutBtn');
    if (!signOutBtn) {
      accountDebug('[account-scripts] Sidebar sign-out button not found (user may not be logged in)');
      return;
    }
    if (signOutBtn._listenerAttached) {
      accountDebug('[account-scripts] Sidebar sign-out button listener already attached');
      return;
    }
    signOutBtn._listenerAttached = true;
    
    signOutBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.handleSignOut === 'function') {
        window.handleSignOut();
      }
    });
    
    accountDebug('[account-scripts] Sidebar sign-out button event listener attached');
  }

  function showSidebarFormError(form, message, fieldIds = []) {
    if (!form) {
      window.alert(message);
      return;
    }
    let region = form.querySelector('[data-form-error]');
    if (!region) {
      region = document.createElement('div');
      region.setAttribute('data-form-error', '1');
      region.setAttribute('role', 'alert');
      region.id = `${form.id || 'sidebarLoginForm'}-error`;
      region.className = 'form-error-alert';
      region.style.cssText =
        'margin:0 0 12px;padding:10px 12px;border-radius:8px;background:#fef2f2;border:1px solid #fecaca;color:#fecaca;font-size:0.875rem;';
      // On navy sidebar, use light text on dark red tint
      region.style.background = 'rgba(127, 29, 29, 0.85)';
      region.style.borderColor = 'rgba(252, 165, 165, 0.5)';
      region.style.color = '#fecaca';
      form.prepend(region);
    }
    region.hidden = false;
    region.textContent = message;
    form.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    for (const id of fieldIds) {
      const field = form.querySelector(`#${id}`);
      if (!field) continue;
      field.setAttribute('aria-invalid', 'true');
      const existing = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!existing.includes(region.id)) {
        existing.push(region.id);
        field.setAttribute('aria-describedby', existing.join(' '));
      }
    }
  }

  function setupSidebarSignInForm() {
    const form = document.getElementById('sidebarLoginForm');
    if (!form) return;

    if (form._sidebarSignInSetup) return;
    form._sidebarSignInSetup = true;

    // Password visibility toggle (form is injected dynamically, so bind here)
    form.querySelectorAll('.password-toggle').forEach((btn) => {
      btn.addEventListener('click', function() {
        const wrapper = btn.closest('.password-input-wrapper');
        const input = wrapper && wrapper.querySelector('input');
        if (!input) return;
        const isPassword = input.type === 'password';
        input.type = isPassword ? 'text' : 'password';
        btn.setAttribute('aria-pressed', isPassword ? 'true' : 'false');
        btn.setAttribute('aria-label', isPassword ? 'Hide password' : 'Show password');
        btn.setAttribute('title', isPassword ? 'Hide password' : 'Show password');
      });
    });

    form.addEventListener('submit', async function(e) {
      e.preventDefault();

      const emailInput = form.querySelector('#sidebarEmail');
      const passwordInput = form.querySelector('#sidebarPassword');
      const email = (emailInput && emailInput.value && emailInput.value.trim()) || '';
      const password = (passwordInput && passwordInput.value) || '';
      const rememberMe = form.querySelector('#sidebarRememberMe');
      const remember = rememberMe ? rememberMe.checked : false;

      if (!email || !password) {
        showSidebarFormError(form, 'Please enter your email and password.', ['sidebarEmail', 'sidebarPassword']);
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
      }

      try {
        if (!window.apiService) {
          throw new Error('API service not available. Please refresh the page and try again.');
        }

        const response = await window.apiService.login(email, password, remember);

        if (response && response.user) {
          const userData = {
            id: response.user.id,
            email: response.user.email,
            username: response.user.username,
            firstName: response.user.firstName || response.user.first_name,
            lastName: response.user.lastName || response.user.last_name,
            lastLogin: new Date().toISOString(),
            reputation: response.user.reputation || 0
          };
          localStorage.setItem('usedfindr_user', JSON.stringify(userData));

          try {
            const fullProfile = await window.apiService.getUserProfile(response.access);
            if (fullProfile) {
              const completeUserData = { ...userData, ...fullProfile };
              localStorage.setItem('usedfindr_user', JSON.stringify(completeUserData));
            }
          } catch (profileError) {
            logger.warn('Could not fetch full profile after sidebar login:', profileError);
          }

          if (window.accountManager) {
            try {
              if (typeof window.accountManager.migrateSavedItemsToAPI === 'function') {
                await window.accountManager.migrateSavedItemsToAPI();
              }
              if (typeof window.accountManager.migrateUserFindsToAPI === 'function') {
                await window.accountManager.migrateUserFindsToAPI();
              }
            } catch (migrationError) {
              logger.warn('Migration after sidebar login:', migrationError);
            }
          }

          if (remember) {
            try { localStorage.setItem('usedfindr_remember_email', email); } catch (err) {}
          } else {
            try { localStorage.removeItem('usedfindr_remember_email'); } catch (err) {}
          }

          loadSidebar();
        } else {
          showSidebarFormError(form, 'Login failed. Please try your credentials again.', ['sidebarEmail', 'sidebarPassword']);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }
        }
      } catch (error) {
        logger.error('Sidebar login error:', error);
        let errorMessage = 'Login failed. Please try again.';
        if (error.message) {
          if (error.message.includes('401') || error.message.includes('Invalid') || error.message.includes('credentials')) {
            errorMessage = 'Invalid email or password. Please try again.';
          } else if (error.message.includes('404') || error.message.includes('not found')) {
            errorMessage = 'No account found with that email. Please create an account first.';
          } else if (error.message.includes('403') || error.message.includes('Forbidden') || error.message.includes('CSRF')) {
            errorMessage = 'Authentication error. Please refresh the page and try again.';
          } else if (error.message.includes('500') || error.message.includes('Internal server error') || error.message.includes('data stream')) {
            errorMessage = 'Server error occurred. The backend may be experiencing issues. Please try again in a moment.';
          } else {
            errorMessage = error.message;
          }
        }
        if (error.status === 500) {
          errorMessage = 'Server error occurred. Please try again in a moment.';
        } else if (error.status === 403) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        }
        showSidebarFormError(form, errorMessage, ['sidebarEmail', 'sidebarPassword']);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    });

    accountDebug('[account-scripts] Sidebar sign-in form setup complete');
  }

  function enableTransitionsAfterLoad() {
    setTimeout(() => {
      document.body.classList.add('account-sidebar-transitions-enabled');
    }, 50);
  }

  // --- Cursor Tooltip Logic ---
  let cursorTooltip = null;

  function createCursorTooltip() {
    if (cursorTooltip) return cursorTooltip;
    
    const tooltip = document.createElement('div');
    tooltip.id = 'cursor-tooltip';
    tooltip.style.cssText = `
      position: fixed; pointer-events: none; background-color: #111827;
      color: #f9fafb; padding: 6px 10px; border-radius: 6px;
      font-size: 13px; white-space: nowrap; z-index: 10000;
      opacity: 0; visibility: hidden;
      transition: opacity 0.15s ease, visibility 0.15s ease;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    `;
    document.body.appendChild(tooltip);
    cursorTooltip = tooltip;
    return tooltip;
  }

  function showCursorTooltip(text, x, y) {
    const tooltip = createCursorTooltip();
    tooltip.textContent = text;
    
    tooltip.style.opacity = '0';
    tooltip.style.visibility = 'visible';
    tooltip.style.left = '-9999px';
    tooltip.style.top = '-9999px';
    
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;
    const offset = 12;
    let left = x + offset;
    let top = y + offset;
    if (left + tooltipWidth > window.innerWidth - 10) left = x - tooltipWidth - offset;
    if (top + tooltipHeight > window.innerHeight - 10) top = y - tooltipHeight - offset;
    if (left < 10) left = 10;
    if (top < 10) top = 10;
    
    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
    tooltip.style.opacity = '1';
  }

  function hideCursorTooltip() {
    if (cursorTooltip) {
      cursorTooltip.style.opacity = '0';
      cursorTooltip.style.visibility = 'hidden';
    }
  }

  function setupCursorTooltips() {
    const sidebarLinks = document.querySelectorAll('.sidebar-link');
    
    sidebarLinks.forEach(link => {
      if (link._cursorTooltipsSetup) return;
      link._cursorTooltipsSetup = true;
      
      link.addEventListener('mouseenter', function(e) {
        // Only show tooltip when sidebar is collapsed
        if (!document.documentElement.classList.contains('account-sidebar-expanded')) {
          const tooltipText = this.getAttribute('data-tooltip');
          if (tooltipText) {
            const moveHandler = (e) => showCursorTooltip(tooltipText, e.clientX, e.clientY);
            this.addEventListener('mousemove', moveHandler);
            this._cursorTooltipHandler = moveHandler;
          }
        }
      });
      
      link.addEventListener('mouseleave', function() {
        hideCursorTooltip();
        if (this._cursorTooltipHandler) {
          this.removeEventListener('mousemove', this._cursorTooltipHandler);
          this._cursorTooltipHandler = null;
        }
      });
    });
  }

  // --- Initialize ---
  // Always load sidebar (for both guests and logged-in users)
  function initializeSidebar() {
    accountDebug('[account-scripts] initializeSidebar called, readyState:', document.readyState, 'body exists:', !!document.body);
    
    // Ensure DOM is ready
    if (document.readyState === 'loading') {
      accountDebug('[account-scripts] DOM still loading, waiting for DOMContentLoaded');
      document.addEventListener('DOMContentLoaded', () => {
        accountDebug('[account-scripts] DOMContentLoaded fired, loading sidebar');
        // Check for admin users on customer-facing pages first
        checkAdminUserAndRedirect();
        // Small delay to ensure body is fully available
        setTimeout(() => {
          loadSidebar();
          addNavbarSignOutButton();
        }, 10);
      });
    } else {
      // DOM is already ready, but add small delay to ensure body is available
      if (document.body) {
        accountDebug('[account-scripts] DOM ready, body exists, loading sidebar immediately');
        // Check for admin users on customer-facing pages first
        checkAdminUserAndRedirect();
        loadSidebar();
        addNavbarSignOutButton();
      } else {
        accountDebug('[account-scripts] DOM ready but body not available, waiting...');
        // Body not ready yet, wait a bit
        setTimeout(() => {
          if (document.body) {
            accountDebug('[account-scripts] Body now available, loading sidebar');
            // Check for admin users on customer-facing pages first
            checkAdminUserAndRedirect();
            loadSidebar();
            addNavbarSignOutButton();
          } else {
            logger.warn('[account-scripts] Body still not available, retrying...');
            // Fallback: try again after a longer delay
            setTimeout(() => {
              accountDebug('[account-scripts] Final retry to load sidebar');
              loadSidebar();
              addNavbarSignOutButton();
            }, 100);
          }
        }, 10);
      }
    }
    
    // Set up a listener to update sidebar if login status changes
    // This handles the case where a user logs in/out after the page loads
    let retryCount = 0;
    const maxRetries = 15; // Try for up to 3 seconds (15 * 200ms)
    
    const checkInterval = setInterval(() => {
      retryCount++;

      // Redesign shell never injects #accountSidebar — don't keep retrying loadSidebar.
      if (hasGlobalRedesignShell()) {
        if (window.location.pathname.includes('account.html')) {
          setupRouter();
        }
        clearInterval(checkInterval);
        return;
      }

      const sidebar = document.getElementById('accountSidebar');
      
      if (sidebar && document.body) {
        const isLoggedIn = checkIfUserLoggedIn();
        const currentIsLoggedIn = sidebar.dataset.userLoggedIn === 'true';
        
        // If login status changed, reload sidebar with correct content
        if (isLoggedIn !== currentIsLoggedIn) {
          sidebar.remove();
          loadSidebar();
          addNavbarSignOutButton();
          clearInterval(checkInterval);
        } else if (window.accountManager || retryCount >= maxRetries) {
          // AccountManager is ready or we've exhausted retries
          clearInterval(checkInterval);
        }
      } else if (!sidebar && document.body) {
        // Sidebar should exist but doesn't - try to load it
        loadSidebar();
        addNavbarSignOutButton();
        if (window.accountManager || retryCount >= maxRetries) {
          clearInterval(checkInterval);
        }
      } else if (window.accountManager || retryCount >= maxRetries) {
        clearInterval(checkInterval);
      }
    }, 200); // Check every 200ms
  }
  
  // Initialize immediately - use multiple strategies to ensure it runs
  accountDebug('[account-scripts] Script loaded, initializing sidebar...');
  accountDebug('[account-scripts] Current readyState:', document.readyState);
  accountDebug('[account-scripts] Body exists:', !!document.body);
  
  // Strategy 1: Try immediately if DOM is ready
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    accountDebug('[account-scripts] Document already ready, initializing now');
    setTimeout(() => initializeSidebar(), 0);
  }
  
  // Strategy 2: Wait for DOMContentLoaded
  if (document.readyState === 'loading') {
    accountDebug('[account-scripts] Document still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => {
      accountDebug('[account-scripts] DOMContentLoaded fired, initializing');
      // Check for admin users on customer-facing pages first
      checkAdminUserAndRedirect();
      setTimeout(() => initializeSidebar(), 0);
    });
  }
  
  // Strategy 3: Also listen for load event as backup
  window.addEventListener('load', () => {
    accountDebug('[account-scripts] Window load event fired, checking sidebar...');
      setTimeout(() => {
        const sidebar = document.getElementById('accountSidebar');
        if (!sidebar && document.body) {
          const hasGlobalShell =
            hasGlobalRedesignShell();
          if (hasGlobalShell) {
            accountDebug('[account-scripts] Load event: Global redesign shell — no legacy account sidebar expected');
            return;
          }
          const currentPath = window.location.pathname;
          const isAdminAuthPage = currentPath.includes('/admin');
          if (!isAdminAuthPage) {
            logger.info('[account-scripts] Load event: Sidebar missing, loading now...');
            initializeSidebar();
          }
        }
      }, 100);
  });
  
  // Strategy 4: Try immediately as fallback (will be a no-op if already initialized)
  try {
    setTimeout(() => {
      accountDebug('[account-scripts] Fallback immediate initialization attempt');
      initializeSidebar();
    }, 0);
  } catch (error) {
    logger.error('[account-scripts] Error in immediate initialization:', error);
  }
  
  // Fallback: Also try to initialize after a short delay to catch any timing issues
  setTimeout(() => {
    if (hasGlobalRedesignShell()) {
      accountDebug('[account-scripts] Fallback skipped: global redesign shell active');
      return;
    }
    const sidebar = document.getElementById('accountSidebar');
    if (!sidebar && document.body) {
      const currentPath = window.location.pathname;
      const isAdminAuthPage = currentPath.includes('/admin');
      if (!isAdminAuthPage) {
        accountDebug('[account-scripts] Fallback: Sidebar not found after initial load, retrying...');
        loadSidebar();
      }
    }
  }, 500);
  
  // When session is invalidated (401 on consumer), re-load sidebar so it shows guest state
  window.addEventListener('auth:sessionInvalidated', () => {
    if (typeof loadSidebar === 'function') {
      loadSidebar();
    }
  });

  // Listen for pageshow event (handles back/forward navigation and page restoration)
  // This fires on EVERY page navigation, including normal links
  window.addEventListener('pageshow', (event) => {
    accountDebug('[account-scripts] pageshow event fired, persisted:', event.persisted);
    const currentPath = window.location.pathname;
    const isAdminAuthPage = currentPath.includes('/admin');
    
    if (isAdminAuthPage) {
      return; // Don't load sidebar on admin auth pages
    }
    
    // Always try to load sidebar on pageshow (handles both cached and fresh pages)
    // This is critical for normal navigation where cached scripts might have old logic
    setTimeout(() => {
      if (!document.body) {
        accountDebug('[account-scripts] pageshow: Body not ready, retrying...');
        setTimeout(() => {
          if (document.body && !document.getElementById('accountSidebar')) {
            accountDebug('[account-scripts] pageshow: Retry - Sidebar not found, loading...');
            loadSidebar();
          }
        }, 100);
        return;
      }

      const pathInner = window.location.pathname;
      if (pathInner.includes('/admin')) {
        accountDebug('[account-scripts] pageshow: Admin auth page, skipping sidebar');
        return;
      }

      if (hasGlobalRedesignShell()) {
        clearSidebarLayoutState();
        accountDebug('[account-scripts] pageshow: global redesign shell — no legacy account sidebar');
        return;
      }

      const sidebar = document.getElementById('accountSidebar');

      if (!sidebar) {
        accountDebug('[account-scripts] pageshow: Sidebar not found, loading...');
        try {
          loadSidebar();
          setTimeout(() => {
            if (!document.getElementById('accountSidebar') && document.body) {
              if (hasGlobalRedesignShell()) {
                clearSidebarLayoutState();
                return;
              }
              accountDebug('[account-scripts] pageshow: Sidebar still missing after loadSidebar, injecting directly...');
              const isLoggedIn = localStorage.getItem('usedfindr_user') ? true : false;
              if (!isLoggedIn) {
                clearSidebarLayoutState();
                return;
              }
              document.body.classList.add('has-account-sidebar');
              document.body.insertAdjacentHTML('afterbegin', loggedInSidebarHTML);
              const injectedSidebar = document.getElementById('accountSidebar');
              if (injectedSidebar) {
                injectedSidebar.dataset.userLoggedIn = isLoggedIn.toString();
                setupSidebarToggle();
                setupSidebarSignOut();
                setupCursorTooltips();
                enableTransitionsAfterLoad();
                accountDebug('[account-scripts] pageshow: Sidebar injected directly and initialized');
              }
            }
          }, 100);
        } catch (error) {
          logger.error('[account-scripts] pageshow: Error in loadSidebar, injecting directly:', error);
          if (hasGlobalRedesignShell()) {
            clearSidebarLayoutState();
            return;
          }
          const isLoggedIn = localStorage.getItem('usedfindr_user') ? true : false;
          if (!isLoggedIn) {
            clearSidebarLayoutState();
            return;
          }
          document.body.classList.add('has-account-sidebar');
          document.body.insertAdjacentHTML('afterbegin', loggedInSidebarHTML);
        }
      } else {
        if (checkIfUserLoggedIn()) {
          document.body.classList.add('has-account-sidebar');
          accountDebug('[account-scripts] pageshow: Sidebar exists, ensuring body class is set');
        } else {
          clearSidebarLayoutState();
          return;
        }

        if (checkIfUserLoggedIn()) {
          if (typeof updateSidebarProfile === 'function') {
            updateSidebarProfile();
          }
        }
      }
    }, 50);
  });
  
  // Also listen for popstate (back/forward buttons) as additional safety
  window.addEventListener('popstate', () => {
    logger.info('[account-scripts] popstate event fired');
    setTimeout(() => {
      const currentPath = window.location.pathname;
      const isAdminAuthPage = currentPath.includes('/admin');
      if (isAdminAuthPage || !document.body) {
        return;
      }
      if (hasGlobalRedesignShell()) {
        clearSidebarLayoutState();
        return;
      }
      const sidebar = document.getElementById('accountSidebar');
      if (!sidebar) {
        logger.info('[account-scripts] popstate: Sidebar not found, loading...');
        loadSidebar();
      }
    }, 10);
  });
  
  // Listen for storage changes (handles login/logout in other tabs, but also helps with current tab)
  window.addEventListener('storage', (event) => {
    if (event.key === 'usedfindr_user' || event.key === 'usedfindr_token' || event.key === 'usedfindr_refresh_token' || event.key === null) {
      // User login status may have changed, reload sidebar
      setTimeout(loadSidebar, 50);
    }
  });
  
  // Also check periodically for login status changes (handles same-tab login/logout)
  // This is a fallback for when storage event doesn't fire in the same tab
  let lastLoginStatus = checkIfUserLoggedIn();
  setInterval(() => {
    const currentLoginStatus = checkIfUserLoggedIn();
    if (currentLoginStatus !== lastLoginStatus) {
      lastLoginStatus = currentLoginStatus;
      // Also remove user data if tokens are gone
      if (!currentLoginStatus) {
        localStorage.removeItem('usedfindr_user');
      }
      loadSidebar();
    }
  }, 500); // Check every 500ms

  // Expose router functions globally for potential external use
  window.accountRouter = {
    navigate: navigateToRoute,
    getCurrentRoute: getCurrentRoute
  };
  
  // Expose loadSidebar globally so it can be called manually if needed
  window.loadAccountSidebar = loadSidebar;
  
  // Track if profile update is in progress to prevent multiple simultaneous calls
  let profileUpdateInProgress = false;
  let profileUpdateTimeout = null;
  let lastProfileFetchTime = 0;
  const PROFILE_FETCH_COOLDOWN = 2000; // Don't fetch more than once per 2 seconds
  
  // Expose lastProfileFetchTime globally so it can be reset when needed
  window.lastProfileFetchTime = 0;
  
  // Function to update sidebar profile with user data
  // options: { forceRefresh: true } to bypass cooldown (e.g. after vote so count updates immediately)
  async function updateSidebarProfile(retryCount = 0, options = {}) {
    const forceRefresh = options && options.forceRefresh === true;
    // Debounce: clear any pending updates
    if (profileUpdateTimeout) {
      clearTimeout(profileUpdateTimeout);
      profileUpdateTimeout = null;
    }
    
    // If update is already in progress, queue this call (pass options so forceRefresh is preserved)
    if (profileUpdateInProgress && retryCount === 0) {
      profileUpdateTimeout = setTimeout(() => updateSidebarProfile(0, options), 100);
      return;
    }
    
    // Prevent redundant API calls within cooldown period (unless retry or forceRefresh e.g. after vote)
    const globalLastFetch = window.lastProfileFetchTime || 0;
    const effectiveLastFetch = Math.max(lastProfileFetchTime, globalLastFetch);
    const now = Date.now();
    if (!forceRefresh && retryCount === 0 && profileUpdateInProgress === false &&
        (now - effectiveLastFetch) < PROFILE_FETCH_COOLDOWN) {
      accountDebug('[account-scripts] Skipping profile update - recently fetched');
      return;
    }
    
    accountDebug(`[account-scripts] updateSidebarProfile called, retryCount: ${retryCount}`);
    
    const profileElement = document.getElementById('sidebarProfile');
    if (!profileElement) {
      // Retry if sidebar not loaded yet (max 10 retries, 100ms apart)
      if (retryCount < 10) {
        accountDebug(`[account-scripts] Sidebar not found, retrying in 100ms...`);
        setTimeout(() => updateSidebarProfile(retryCount + 1), 100);
      }
      return;
    }
    
    // Wait for accountManager to be available (max 20 retries, 100ms apart = 2 seconds)
    if (!window.accountManager || !window.accountManager.currentUser) {
      if (retryCount < 20) {
        accountDebug(`[account-scripts] accountManager not ready, retrying in 100ms... (retry ${retryCount + 1}/20)`);
        setTimeout(() => updateSidebarProfile(retryCount + 1), 100);
        return;
      }
      // If still not available after retries, try to get user from localStorage
      accountDebug('[account-scripts] accountManager not available after retries, using localStorage fallback');
      const userData = localStorage.getItem('usedfindr_user');
      if (!userData) {
        logger.info('[account-scripts] No user data in localStorage');
        return; // No user data available
      }
      try {
        const user = JSON.parse(userData);
        
        // Helper function to get display name
        const getDisplayName = (userData) => {
          if (userData.firstName || userData.first_name) {
            const firstName = userData.firstName || userData.first_name || '';
            const lastName = userData.lastName || userData.last_name || '';
            const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
            if (fullName) return fullName;
          }
          // Prioritize email if no name
          if (userData.email) {
            return userData.email.split('@')[0]; // Use local part, e.g., "robbbs+test1113"
          }
          // Fallback to username
          if (userData.username) {
            return userData.username;
          }
          return 'User';
        };
        
        const displayName = getDisplayName(user);
        
        const nameElement = document.getElementById('sidebarProfileName');
        if (nameElement) {
          nameElement.textContent = displayName;
          accountDebug('[account-scripts] Updated sidebar name from localStorage:', displayName);
        }
        
        const accountNameElement = document.getElementById('userName');
        if (accountNameElement) {
          accountNameElement.textContent = displayName;
          accountDebug('[account-scripts] Updated account page name from localStorage:', displayName);
        }
        
        // Try to update other fields from localStorage if available
        const reputationElement = document.getElementById('sidebarProfileReputation');
        if (reputationElement && user.reputation !== undefined) {
          reputationElement.textContent = `${user.reputation || 0} reputation`;
        }
        
        const itemsElement = document.getElementById('sidebarStatItems');
        if (itemsElement && user.itemsSubmittedCount !== undefined) {
          itemsElement.textContent = user.itemsSubmittedCount || 0;
        }
        
        const votesElement = document.getElementById('sidebarStatVotes');
        if (votesElement && user.votesCastCount !== undefined) {
          votesElement.textContent = user.votesCastCount || 0;
        }
      } catch (e) {
        logger.debug('Could not parse user data from localStorage:', e);
      }
      return;
    }
    
    const user = window.accountManager.currentUser;
    accountDebug('[account-scripts] accountManager available, user:', user.username || user.id);
    
    // Helper function to get display name
    const getDisplayName = (userData) => {
      if (userData.firstName || userData.first_name) {
        const firstName = userData.firstName || userData.first_name || '';
        const lastName = userData.lastName || userData.last_name || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        if (fullName) return fullName;
      }
      // Fallback to username (but not email if it looks like an email)
      if (userData.username && !userData.username.includes('@')) {
        return userData.username;
      }
      return 'User';
    };
    
    const displayName = getDisplayName(user);
    
    // Only update name immediately if we have a good name (not "User")
    // Otherwise, wait for API response to avoid flickering
    const nameElement = document.getElementById('sidebarProfileName');
    const accountNameElement = document.getElementById('userName');
    const currentName = nameElement ? nameElement.textContent.trim() : '';
    
    if (displayName !== 'User' && displayName !== currentName) {
      // We have a good name and it's different, update immediately
      if (nameElement) {
        nameElement.textContent = displayName;
        accountDebug('[account-scripts] Updated sidebar name:', displayName);
      }
      if (accountNameElement) {
        accountNameElement.textContent = displayName;
        accountDebug('[account-scripts] Updated account page name:', displayName);
      }
    } else if (displayName === 'User' && currentName && currentName !== 'User' && currentName !== 'Loading...') {
      // Don't overwrite a good name with "User" - wait for API
      accountDebug('[account-scripts] Keeping current name, waiting for API response');
    }
    
    // Try to fetch profile from API
    try {
      // Wait for apiService to be available (max 5 additional retries)
      if (!window.apiService && retryCount < 25) {
        accountDebug(`[account-scripts] apiService not ready, retrying in 100ms... (retry ${retryCount + 1}/25)`);
        setTimeout(() => updateSidebarProfile(retryCount + 1), 100);
        return;
      }
      
      if (window.apiService) {
        // Mark update as in progress and update timestamp
        profileUpdateInProgress = true;
        lastProfileFetchTime = Date.now();
        window.lastProfileFetchTime = Date.now();
        
        accountDebug('[account-scripts] Fetching profile from API...');
        const profile = await window.apiService.getUserProfile();
        if (profile) {
          accountDebug('[account-scripts] Profile fetched successfully:', {
            reputation: profile.reputation,
            itemsSubmittedCount: profile.itemsSubmittedCount,
            votesCastCount: profile.votesCastCount
          });
          
          // Helper function to get display name from profile
          const getDisplayNameFromProfile = (profileData) => {
            if (profileData.firstName || profileData.first_name) {
              const firstName = profileData.firstName || profileData.first_name || '';
              const lastName = profileData.lastName || profileData.last_name || '';
              const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
              if (fullName) return fullName;
            }
            // Fallback to username (but not email if it looks like an email)
            if (profileData.username && !profileData.username.includes('@')) {
              return profileData.username;
            }
            return 'User';
          };
          
          const displayName = getDisplayNameFromProfile(profile);
          
          // Only update if the name actually changed
          const nameElement = document.getElementById('sidebarProfileName');
          const accountNameElement = document.getElementById('userName');
          const currentName = nameElement ? nameElement.textContent.trim() : '';
          
          if (displayName !== currentName) {
            // Update name in sidebar
            if (nameElement) {
              nameElement.textContent = displayName;
              accountDebug('[account-scripts] Updated sidebar name from API:', displayName);
            }
            
            // Update name on account page
            if (accountNameElement) {
              accountNameElement.textContent = displayName;
              accountDebug('[account-scripts] Updated account page name from API:', displayName);
            }
          }
          
          // Update level
          const levelElement = document.getElementById('sidebarProfileLevel');
          if (levelElement) {
            levelElement.textContent = profile.levelTitle || 'Newfinder';
          }
          
          // Update reputation
          const reputationElement = document.getElementById('sidebarProfileReputation');
          if (reputationElement) {
            reputationElement.textContent = `${profile.reputation || 0} reputation`;
          }
          
          // Update stats
          const itemsElement = document.getElementById('sidebarStatItems');
          if (itemsElement) {
            itemsElement.textContent = profile.itemsSubmittedCount || 0;
          }
          
          const votesElement = document.getElementById('sidebarStatVotes');
          if (votesElement) {
            votesElement.textContent = profile.votesCastCount || 0;
          }
          
          // Update badges (show top 3) — safe insertion: no innerHTML with API data (Phase 2 XSS)
          const badgesElement = document.getElementById('sidebarProfileBadges');
          if (badgesElement && profile.earnedBadges && profile.earnedBadges.length > 0) {
            const topBadges = profile.earnedBadges.slice(0, 3);
            badgesElement.replaceChildren();
            topBadges.forEach(userBadge => {
              const badge = userBadge.badge;
              const span = document.createElement('span');
              span.className = 'sidebar-badge-icon';
              span.setAttribute('title', badge.badgeName ?? '');
              span.textContent = badge.icon ?? '';
              badgesElement.appendChild(span);
            });
          } else if (badgesElement) {
            badgesElement.replaceChildren();
          }
          
          // Update profile icon/avatar — safe insertion: textContent only (Phase 2 XSS)
          const profileAvatar = document.querySelector('.sidebar-profile-avatar');
          if (profileAvatar) {
            const profileIcon = profile.profileIcon || profile.profile_icon || '👤';
            const span = document.createElement('span');
            span.className = 'sidebar-profile-icon-emoji';
            span.textContent = profileIcon;
            profileAvatar.replaceChildren(span);
            profileAvatar.classList.add('has-icon');
          }
          
          accountDebug('[account-scripts] Sidebar profile updated successfully from API');
          profileUpdateInProgress = false;
          return;
        }
        profileUpdateInProgress = false;
      }
    } catch (error) {
      logger.debug('Could not fetch profile for sidebar:', error);
      profileUpdateInProgress = false;
    }
    
    // Fallback to localStorage data
    accountDebug('[account-scripts] Using fallback data from localStorage/user object');
    if (window.reputationManager && user.reputation !== undefined) {
      const level = await window.reputationManager.getUserLevel(user.id);
      const levelElement = document.getElementById('sidebarProfileLevel');
      if (levelElement) {
        levelElement.textContent = level;
      }
      
      const reputationElement = document.getElementById('sidebarProfileReputation');
      if (reputationElement) {
        reputationElement.textContent = `${user.reputation || 0} reputation`;
      }
    }
    
    // Fallback: try to get stats from user object
    const itemsElement = document.getElementById('sidebarStatItems');
    if (itemsElement && user.itemsSubmittedCount !== undefined) {
      itemsElement.textContent = user.itemsSubmittedCount || 0;
    }
    
    const votesElement = document.getElementById('sidebarStatVotes');
    if (votesElement && user.votesCastCount !== undefined) {
      votesElement.textContent = user.votesCastCount || 0;
    }
    
    // Clear loading states
    const loadingElements = document.querySelectorAll('[id*="sidebarProfile"], [id*="sidebarStat"]');
    loadingElements.forEach(el => {
      if (el.textContent === 'Loading...') {
        // Update will have set it, but ensure
      }
    });
    
    // Final force update to clear Loading...
    const finalDisplayName = getDisplayName(user || profile || {}); // Reuse helper, pass available data
    const finalNameElement = document.getElementById('sidebarProfileName');
    if (finalNameElement && finalNameElement.textContent === 'Loading...') {
      finalNameElement.textContent = finalDisplayName;
      accountDebug('[account-scripts] Force cleared Loading... to:', finalDisplayName);
    }
    
    // Reset flag when done
    profileUpdateInProgress = false;
    
    accountDebug('[account-scripts] Sidebar profile update complete');
  }
  
  // Expose updateSidebarProfile globally
  window.updateSidebarProfile = updateSidebarProfile;
  
  // Function to update sidebar vote count with optimistic UI update
  function updateSidebarVoteCount(delta) {
    const votesElement = document.getElementById('sidebarStatVotes');
    if (!votesElement) {
      // Sidebar element not found - this is fine for guest users or when sidebar isn't loaded
      accountDebug('[account-scripts] sidebarStatVotes element not found, skipping vote count update');
      return;
    }
    
    try {
      // Get current vote count from text content
      const currentText = votesElement.textContent || '0';
      // Parse to integer, defaulting to 0 if invalid
      const currentCount = parseInt(currentText, 10) || 0;
      
      // Calculate new count
      const newCount = Math.max(0, currentCount + delta); // Ensure non-negative
      
      // Update the display
      votesElement.textContent = newCount;
      
      logger.info(`[account-scripts] Updated sidebar vote count: ${currentCount} → ${newCount} (delta: ${delta})`);
    } catch (error) {
      logger.warn('[account-scripts] Error updating sidebar vote count:', error);
      // Don't throw - this is a non-critical UI update
    }
  }
  
  function updateSidebarReputation(delta) {
    const reputationElement = document.getElementById('sidebarProfileReputation');
    if (!reputationElement) {
      // Sidebar element not found - this is fine for guest users or when sidebar isn't loaded
      accountDebug('[account-scripts] sidebarProfileReputation element not found, skipping reputation update');
      return;
    }
    
    try {
      // Get current reputation from text content (format: "X reputation" or just "X")
      const currentText = reputationElement.textContent || '0 reputation';
      // Extract number from text (handles both "3 reputation" and "3" formats)
      const match = currentText.match(/(\d+)/);
      const currentReputation = match ? parseInt(match[1], 10) : 0;
      
      // Calculate new reputation
      const newReputation = Math.max(0, currentReputation + delta); // Ensure non-negative
      
      // Update the display
      reputationElement.textContent = `${newReputation} reputation`;
      
      logger.info(`[account-scripts] Updated sidebar reputation: ${currentReputation} → ${newReputation} (delta: ${delta})`);
    } catch (error) {
      logger.warn('[account-scripts] Error updating sidebar reputation:', error);
      // Don't throw - this is a non-critical UI update
    }
  }
  
  // Expose updateSidebarVoteCount and updateSidebarReputation globally
  window.updateSidebarVoteCount = updateSidebarVoteCount;
  window.updateSidebarReputation = updateSidebarReputation;
  
  /**
   * Handle user sign out with confirmation dialog
   * This function is available globally and can be called from navbar or sidebar
   */
  async function handleSignOut() {
    // Show confirmation dialog
    const confirmed = confirm('Are you sure you want to sign out?');
    
    if (!confirmed) {
      return;
    }
    
    try {
      // Call logout API if available
      if (window.apiService && typeof window.apiService.logout === 'function') {
        await window.apiService.logout();
        accountDebug('[account-scripts] API logout successful');
      } else if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
        window.apiService.clearAccessToken();
      }
      // Explicitly clear user data (clearAccessToken no longer clears usedfindr_user)
      localStorage.removeItem('usedfindr_user');
      accountDebug('[account-scripts] Cleared authentication data');
      
      // Redirect to home page with success indication
      window.location.href = 'index.html';
      
    } catch (error) {
      logger.error('[account-scripts] Error during sign out:', error);
      if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
        window.apiService.clearAccessToken();
      }
      localStorage.removeItem('usedfindr_user');
      alert('Signed out successfully.');
      window.location.href = 'index.html';
    }
  }
  
  // Expose handleSignOut globally
  window.handleSignOut = handleSignOut;
  
  /**
   * Add sign-out button to navbar when user is logged in
   * This function checks authentication status and injects button dynamically
   */
  function addNavbarSignOutButton() {
    accountDebug('[account-scripts] addNavbarSignOutButton called');

    if (document.querySelector('.app[data-site-shell="1"]')) {
      accountDebug('[account-scripts] Console shell — skip legacy .nav-links sign-out injection');
      return;
    }
    
    // Check if user is logged in
    if (!checkIfUserLoggedIn()) {
      accountDebug('[account-scripts] User not logged in, skipping navbar sign-out button');
      return;
    }
    
    // If profile dropdown is present (from script.js updateNavigation), do not add duplicate Sign Out
    if (document.getElementById('navProfileWrap')) {
      accountDebug('[account-scripts] Profile dropdown already present, skipping sign-out button');
      return;
    }
    
    const navLinks = document.querySelector('.nav-links');
    if (!navLinks) {
      accountDebug('[account-scripts] .nav-links not found, cannot add sign-out button');
      return;
    }
    
    if (document.getElementById('navbarSignOutBtn')) {
      accountDebug('[account-scripts] Navbar sign-out button already exists');
      return;
    }
    
    // Create sign-out button
    const signOutBtn = document.createElement('button');
    signOutBtn.id = 'navbarSignOutBtn';
    signOutBtn.className = 'nav-link navbar-signout-btn';
    signOutBtn.textContent = 'Sign Out';
    signOutBtn.title = 'Sign out of your account';
    signOutBtn.onclick = function() {
      if (typeof window.handleSignOut === 'function') {
        window.handleSignOut();
      }
    };
    
    // Append to nav-links
    navLinks.appendChild(signOutBtn);
    accountDebug('[account-scripts] Navbar sign-out button added successfully');
  }
  
  // Expose addNavbarSignOutButton globally
  window.addNavbarSignOutButton = addNavbarSignOutButton;
  
  // Watch for accountManager to become available and update profile
  (function watchForAccountManager() {
    if (document.querySelector('.app[data-site-shell="1"]') && !document.getElementById('accountSidebar')) {
      accountDebug('[account-scripts] No account sidebar on this page — skip sidebar profile watch');
      return;
    }

    let checkCount = 0;
    const maxChecks = 50; // Check for 5 seconds (50 * 100ms)
    
    const checkInterval = setInterval(() => {
      checkCount++;
      
      // If accountManager is available and sidebar exists, update profile
      if (window.accountManager && window.accountManager.currentUser) {
        const sidebar = document.getElementById('accountSidebar');
        if (sidebar && checkIfUserLoggedIn()) {
          accountDebug('[account-scripts] accountManager now available, updating sidebar profile');
          if (typeof updateSidebarProfile === 'function') {
            updateSidebarProfile();
          }
          clearInterval(checkInterval);
          return;
        }
      }
      
      // Stop checking after max attempts
      if (checkCount >= maxChecks) {
        logger.info('[account-scripts] Stopped watching for accountManager after max checks');
        clearInterval(checkInterval);
      }
    }, 100); // Check every 100ms
  })();
  
  logger.info('[account-scripts] Script initialization complete. loadAccountSidebar available globally.');
  })(); // End IIFE
  
} catch (error) {
  logger.error('[account-scripts] FATAL ERROR in script execution:', error);
  logger.error('[account-scripts] Error stack:', error.stack);
  // Try to load sidebar anyway as a fallback (IIFE failed — no access to inner helpers)
  setTimeout(function() {
      function stripLegacyAccountSidebar() {
        const el = document.getElementById('accountSidebar');
        if (el) el.remove();
        if (document.body) {
          document.body.classList.remove('has-account-sidebar', 'account-sidebar-transitions-enabled', 'mobile-menu-open');
          document.body.style.overflow = '';
        }
        document.documentElement.classList.remove('account-sidebar-expanded');
      }
      const currentPath = window.location.pathname;
      const isAdminAuthPage = currentPath.includes('/admin');
      const hasGlobalShellEm = !!document.querySelector('.app[data-site-shell="1"]');
      if (hasGlobalShellEm) {
        stripLegacyAccountSidebar();
        return;
      }
      if (document.body && !document.getElementById('accountSidebar') && !isAdminAuthPage) {
        logger.info('[account-scripts] Attempting emergency sidebar load after error...');
        try {
          const isLoggedIn = (window.apiService && typeof window.apiService.getAccessToken === 'function' && window.apiService.getAccessToken()) ? true : false;
          if (!isLoggedIn) {
            stripLegacyAccountSidebar();
            return;
          }
          document.body.classList.add('has-account-sidebar');
          document.body.insertAdjacentHTML('afterbegin', '<aside class="account-sidebar" id="accountSidebar"><button class="sidebar-toggle" id="account-sidebar-toggle">☰</button><nav class="sidebar-site-nav"><button class="sidebar-link sidebar-add-item-btn" id="sidebarAddItemBtn" data-action="open-add-item-modal">➕ Add Item</button><a href="trusted-finder.html" class="sidebar-link">Trusted Finder</a><a href="search.html" class="sidebar-link">Search</a><a href="categories.html" class="sidebar-link">Categories</a></nav><div class="sidebar-header"><h2>My Account</h2></div><nav class="sidebar-nav"><a href="account.html#dashboard" class="sidebar-link">Dashboard</a></nav></aside>');
        } catch (e) {
          logger.error('[account-scripts] Emergency sidebar load also failed:', e);
        }
      }
  }, 1000);
}

// Final safety check - run after a delay to ensure everything is loaded
setTimeout(function() {
  if (typeof window.loadAccountSidebar === 'function') {
    accountDebug('[account-scripts] Final safety check: loadAccountSidebar is available');
    if (document.querySelector('.app[data-site-shell="1"]')) {
      accountDebug('[account-scripts] Final safety check: skipped (Console shell)');
      return;
    }
    const sidebar = document.getElementById('accountSidebar');
    if (!sidebar && document.body) {
      const currentPath = window.location.pathname;
      const isAdminAuthPage = currentPath.includes('/admin');
      if (!isAdminAuthPage) {
        accountDebug('[account-scripts] Final safety check: Sidebar missing, loading now...');
        window.loadAccountSidebar();
      }
    }
  } else {
    logger.error('[account-scripts] ERROR: loadAccountSidebar function not available! Script may have failed to load.');
  }
}, 1000);

